/**
 * URL Validation Utility
 *
 * Validates and sanitizes URLs to prevent XSS attacks
 * Only allows safe protocols: http, https, mailto
 *
 * Security: Prevents XSS via javascript:, data:, vbscript: protocols
 * OWASP Top 10 #7: Cross-Site Scripting (XSS)
 */

const SAFE_URL_PROTOCOLS = ['http:', 'https:', 'mailto:'];

/**
 * Validates if a URL is safe to use in links
 * Prevents XSS via javascript:, data:, vbscript: protocols
 *
 * @param url - The URL to validate
 * @returns true if URL is safe, false otherwise
 *
 * @example
 * isSafeUrl('https://example.com') // true
 * isSafeUrl('javascript:alert("xss")') // false
 */
export function isSafeUrl(url: string): boolean {
  if (!url || typeof url !== 'string') {
    return false;
  }

  const trimmedUrl = url.trim();

  // Empty URLs are not safe
  if (trimmedUrl.length === 0) {
    return false;
  }

  try {
    // If URL doesn't have a protocol, prepend https://
    const urlWithProtocol = trimmedUrl.includes('://') ? trimmedUrl : `https://${trimmedUrl}`;

    const parsedUrl = new URL(urlWithProtocol);

    // Check if protocol is in safe list
    return SAFE_URL_PROTOCOLS.includes(parsedUrl.protocol);
  } catch {
    // Invalid URL format
    return false;
  }
}

/**
 * Sanitizes a URL by ensuring it has a safe protocol
 * Returns the sanitized URL or null if invalid
 *
 * @param url - The URL to sanitize
 * @returns Sanitized URL or null if invalid
 *
 * @example
 * sanitizeUrl('example.com') // 'https://example.com'
 * sanitizeUrl('javascript:alert("xss")') // null
 */
export function sanitizeUrl(url: string): string | null {
  if (!url || typeof url !== 'string') {
    return null;
  }

  const trimmedUrl = url.trim();

  if (trimmedUrl.length === 0) {
    return null;
  }

  // If it's a mailto link, return as-is if valid
  if (trimmedUrl.startsWith('mailto:')) {
    return isSafeUrl(trimmedUrl) ? trimmedUrl : null;
  }

  // If URL doesn't have a protocol, prepend https://
  const urlWithProtocol = trimmedUrl.includes('://') ? trimmedUrl : `https://${trimmedUrl}`;

  // Validate and return
  return isSafeUrl(urlWithProtocol) ? urlWithProtocol : null;
}

/**
 * Validates URL format (basic check)
 *
 * @param url - The URL to validate
 * @returns true if URL format is valid, false otherwise
 *
 * @example
 * isValidUrlFormat('https://example.com') // true
 * isValidUrlFormat('example.com') // true
 * isValidUrlFormat('not a url') // false
 */
export function isValidUrlFormat(url: string): boolean {
  if (!url || typeof url !== 'string') {
    return false;
  }

  const trimmedUrl = url.trim();

  // Basic URL format check (domain with optional protocol)
  const urlPattern = /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/i;
  const emailPattern = /^mailto:[^\s@]+@[^\s@]+\.[^\s@]+$/i;

  return urlPattern.test(trimmedUrl) || emailPattern.test(trimmedUrl);
}

/**
 * True for absolute http(s) URLs and genuinely same-origin relative paths
 * ("/uploads/a.pdf"). Relative inputs are resolved against the current origin
 * and must stay on it, so protocol-relative ("//evil.com") and "/\\evil.com"
 * are rejected. Rejects javascript:, data:, blob: and anything unparsable.
 * Use before window.open() or putting a URL into an iframe src.
 */
export function isHttpOrRelativeUrl(url: string): boolean {
  if (!url || typeof url !== 'string') return false;
  const trimmed = url.trim();
  try {
    const parsed = new URL(trimmed, window.location.origin);
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return false;
    if (/^https?:\/\//i.test(trimmed)) return true;
    return parsed.origin === window.location.origin;
  } catch {
    return false;
  }
}
